import { type IConfig } from '@cian/config/shared';
import { type IHttpApi } from '@cian/http-api';
import { type ILogger } from '@cian/logger';
import { useCallback, useEffect, useState } from 'react';

import { mapCalltrackingPhoneToIPhone, requestOfferDynamicPhone } from '../api/offerDynamicCalltracking';
import { type IPhone } from '../types/offer';

import { DYNAMIC_PHONE_TOOLTIP_DURATION_MS, takeDynamicPhoneTooltipShow } from './dynamicPhoneTooltip';
import { useDynamicPhoneErrorToast } from './useDynamicPhoneErrorToast';

interface IRequestParams {
  offerId: number;
  clickId: string;
  hasStaticPhones: boolean;
  /** Событие деградации: КТ не ответил, показана статика */
  onDegradation(): void;
}

interface IDynamicPhoneFlow {
  /** Подменные номера; null — показываем статику */
  dynamicPhones: IPhone[] | null;
  isTooltipVisible: boolean;
  /** false — показывать нечего, блок раскрывать нельзя */
  requestPhone(params: IRequestParams): Promise<boolean>;
}

export function useDynamicPhoneFlow(deps: { config: IConfig; httpApi: IHttpApi; logger: ILogger }): IDynamicPhoneFlow {
  const { config, httpApi, logger } = deps;
  const showPhoneErrorToast = useDynamicPhoneErrorToast();

  const [dynamicPhones, setDynamicPhones] = useState<IPhone[] | null>(null);
  const [isTooltipVisible, setTooltipVisible] = useState(false);

  useEffect(() => {
    if (!isTooltipVisible) {
      return;
    }

    const timer = window.setTimeout(() => setTooltipVisible(false), DYNAMIC_PHONE_TOOLTIP_DURATION_MS);

    return () => window.clearTimeout(timer);
  }, [isTooltipVisible]);

  const showTooltip = useCallback(() => {
    if (takeDynamicPhoneTooltipShow()) {
      setTooltipVisible(true);
    }
  }, []);

  const requestPhone = useCallback(
    async ({ offerId, clickId, hasStaticPhones, onDegradation }: IRequestParams): Promise<boolean> => {
      const result = await requestOfferDynamicPhone({ offerId, clickId }, { config, httpApi, logger });

      if (result?.status === 'ok') {
        setDynamicPhones(result.phones.map(mapCalltrackingPhoneToIPhone));
        showTooltip();

        return true;
      }

      if (!hasStaticPhones) {
        logger.warning('OfferDynamicCalltracking: номер недоступен, показан тост-ошибка', {
          offerId,
          clickId,
          status: result?.status ?? null,
        });
        showPhoneErrorToast();

        return false;
      }

      if (result?.status === 'failedToAllocatePhone') {
        // TODO SEL-998: по контракту реальный номер не раскрывается — ждём чат-фоллбэк
        logger.warning('OfferDynamicCalltracking: failedToAllocatePhone, показана статика', { offerId, clickId });
      } else {
        onDegradation();
      }

      showTooltip();

      return true;
    },
    [config, httpApi, logger, showPhoneErrorToast, showTooltip],
  );

  return { dynamicPhones, isTooltipVisible, requestPhone };
}
